import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import {
  Search,
  Download,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Check,
  Play,
  X,
  MessageSquare,
  Eye,
  ExternalLink,
  CreditCard,
  Plus,
  Loader2,
  CalendarClock,
} from 'lucide-react';

// shadcn/ui components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import {
  BookingListItem,
  BookingFilters,
  DateRangePreset,
  BookingStatus,
  PaymentStatus,
  BookingSortField,
  DEFAULT_BOOKING_FILTERS,
  DATE_RANGE_LABELS,
  BOOKING_SOURCE_LABELS,
  BOOKING_SOURCE_ICONS,
  calculateBookingStats,
  formatDuration,
  formatCurrency,
  formatBookingDate,
  getDateRangeFromPreset,
  PAGE_SIZE_OPTIONS,
} from '@/types/bookings';
import { apiFetch } from '@/lib/operator-api';

export const Route = createFileRoute('/_auth/operator/bookings/')({
  component: BookingsPage,
});

// API response types
interface BookingsApiResponse {
  success: boolean;
  data: BookingListItem[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  error?: string;
}

interface StatusUpdateResponse {
  success: boolean;
  error?: string;
}

// Fetch bookings from API
async function fetchBookingsFromApi(businessId?: string): Promise<BookingListItem[]> {
  const params = new URLSearchParams();
  if (businessId) {
    params.set('businessId', businessId);
  }
  params.set('pageSize', '100'); // Fetch more for client-side filtering

  const response = await apiFetch(`/api/operator/bookings?${params.toString()}`);
  const data: BookingsApiResponse = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch bookings');
  }

  // Transform dates
  return data.data.map(b => ({
    ...b,
    scheduledAt: new Date(b.scheduledAt),
    endsAt: new Date(b.endsAt),
    createdAt: new Date(b.createdAt),
    updatedAt: b.updatedAt ? new Date(b.updatedAt) : undefined,
  }));
}

// Update booking status via API
async function updateBookingStatus(
  id: string,
  newStatus: BookingStatus,
  cancellationReason?: string
): Promise<{ success: boolean; error?: string }> {
  const response = await apiFetch(`/api/operator/bookings/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({
      status: newStatus,
      cancellationReason,
    }),
  });
  const data: StatusUpdateResponse = await response.json();
  return data;
}

// ============================================================================
// Status Badge Mapping
// ============================================================================

function getStatusBadgeVariant(status: BookingStatus) {
  const map: Record<BookingStatus, "pending" | "confirmed" | "inProgress" | "completed" | "cancelled" | "noShow"> = {
    PENDING: 'pending',
    CONFIRMED: 'confirmed',
    IN_PROGRESS: 'inProgress',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
    NO_SHOW: 'noShow',
  };
  return map[status];
}

function getPaymentBadgeVariant(status: PaymentStatus) {
  const map: Record<PaymentStatus, "paid" | "unpaid" | "failed" | "refunded" | "waived"> = {
    PENDING: 'unpaid',
    PAID: 'paid',
    FAILED: 'failed',
    REFUNDED: 'refunded',
    WAIVED: 'waived',
  };
  return map[status];
}

// ============================================================================
// Main Bookings Page Component
// ============================================================================

function BookingsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();

  // State
  const [bookings, setBookings] = useState<BookingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [filters, setFilters] = useState<BookingFilters>(DEFAULT_BOOKING_FILTERS);

  // Fetch bookings from API
  useEffect(() => {
    if (!selectedBusinessId) {
      setBookings([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    async function fetchBookings() {
      setLoading(true);
      setFetchError(null);
      try {
        const data = await fetchBookingsFromApi(selectedBusinessId || undefined);
        if (controller.signal.aborted) return;
        setBookings(data);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        if (controller.signal.aborted) return;
        console.error('Failed to fetch bookings:', error);
        setFetchError(error instanceof Error ? error.message : 'Failed to load bookings');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    fetchBookings();
    return () => controller.abort();
  }, [selectedBusinessId]);
  const [selectedBooking, setSelectedBooking] = useState<BookingListItem | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Filter bookings
  const filteredBookings = useMemo(() => {
    let result = [...bookings];

    // Apply business filter
    if (selectedBusinessId) {
      result = result.filter((b) => b.businessId === selectedBusinessId);
    } else if (filters.businessId) {
      result = result.filter((b) => b.businessId === filters.businessId);
    }

    // Apply search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(
        (b) =>
          b.customerName.toLowerCase().includes(query) ||
          b.customerPhone?.toLowerCase().includes(query) ||
          b.customerEmail?.toLowerCase().includes(query) ||
          b.serviceName.toLowerCase().includes(query) ||
          b.id.toLowerCase().includes(query)
      );
    }

    // Apply date range
    const { start, end } = getDateRangeFromPreset(filters.dateRange.preset);
    const startDate = filters.dateRange.startDate || start;
    const endDate = filters.dateRange.endDate || end;
    result = result.filter((b) => b.scheduledAt >= startDate && b.scheduledAt <= endDate);

    // Apply status filter
    if (filters.statuses.length > 0) {
      result = result.filter((b) => filters.statuses.includes(b.status));
    }

    // Apply payment status filter
    if (filters.paymentStatuses.length > 0) {
      result = result.filter((b) => filters.paymentStatuses.includes(b.paymentStatus));
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (filters.sortBy) {
        case 'scheduledAt':
          comparison = a.scheduledAt.getTime() - b.scheduledAt.getTime();
          break;
        case 'createdAt':
          comparison = a.createdAt.getTime() - b.createdAt.getTime();
          break;
        case 'customerName':
          comparison = a.customerName.localeCompare(b.customerName);
          break;
        case 'serviceName':
          comparison = a.serviceName.localeCompare(b.serviceName);
          break;
        case 'price':
          comparison = a.totalPrice - b.totalPrice;
          break;
        default:
          comparison = 0;
      }
      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [bookings, filters, selectedBusinessId]);

  // Calculate stats
  const stats = useMemo(() => calculateBookingStats(filteredBookings), [filteredBookings]);

  // Paginate
  const paginatedBookings = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredBookings.slice(start, start + pageSize);
  }, [filteredBookings, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredBookings.length / pageSize);

  // Handlers
  const handleFilterChange = useCallback((key: keyof BookingFilters, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleDateRangeChange = useCallback((preset: DateRangePreset) => {
    setFilters((prev) => ({
      ...prev,
      dateRange: { ...prev.dateRange, preset, startDate: null, endDate: null },
    }));
    setCurrentPage(1);
  }, []);

  const toggleStatus = useCallback((status: BookingStatus) => {
    setFilters((prev) => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status],
    }));
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((field: BookingSortField) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  const handleViewDetails = useCallback((booking: BookingListItem) => {
    setSelectedBooking(booking);
    setIsDetailOpen(true);
  }, []);

  const handleViewFullDetails = useCallback((bookingId: string) => {
    navigate({ to: `/operator/bookings/${bookingId}` });
  }, [navigate]);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_BOOKING_FILTERS);
    setCurrentPage(1);
  }, []);

  // Inline error feedback state (replaces alert() calls)
  const [createError, setCreateError] = useState<string | null>(null);
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelBookingId, setCancelBookingId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  // Create booking state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm, setCreateForm] = useState({ customerPhone: '', customerName: '', serviceId: '', staffId: '', date: '', time: '', notes: '' });
  const [availableServices, setAvailableServices] = useState<Array<{ id: string; name: string; durationMinutes: number; priceCents: number }>>([]);
  const [availableStaff, setAvailableStaff] = useState<Array<{ id: string; name: string }>>([]);

  // Reschedule state
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({ date: '', time: '', staffId: '', notes: '' });
  const [rescheduleBookingId, setRescheduleBookingId] = useState<string | null>(null);

  // Fetch services and staff when create dialog opens
  useEffect(() => {
    if (!isCreateOpen || !selectedBusinessId) return;
    const controller = new AbortController();
    (async () => {
      try {
        const [svcRes, staffRes] = await Promise.all([
          apiFetch(`/api/operator/services?businessId=${selectedBusinessId}`, { signal: controller.signal }),
          apiFetch(`/api/operator/staff?businessId=${selectedBusinessId}`, { signal: controller.signal }),
        ]);
        if (controller.signal.aborted) return;
        const svcData = await svcRes.json();
        const staffData = await staffRes.json();
        if (svcData.success) setAvailableServices(svcData.data || []);
        if (staffData.success) setAvailableStaff((staffData.data || []).filter((s: { active?: boolean }) => s.active !== false));
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        console.error('Failed to load services/staff', e);
      }
    })();
    return () => controller.abort();
  }, [isCreateOpen, selectedBusinessId]);

  // Create booking handler
  const handleCreateBooking = useCallback(async () => {
    setCreateError(null);
    if (!selectedBusinessId || !createForm.customerPhone || !createForm.serviceId || !createForm.date || !createForm.time) {
      setCreateError(t('bookings.fillRequiredFields', 'Please fill in required fields: phone, service, date, and time'));
      return;
    }
    setCreateLoading(true);
    try {
      const service = availableServices.find(s => s.id === createForm.serviceId);
      const startTime = new Date(`${createForm.date}T${createForm.time}`);
      const response = await apiFetch('/api/operator/bookings/walk-in', {
        method: 'POST',
        body: JSON.stringify({
          businessId: selectedBusinessId,
          serviceId: createForm.serviceId,
          staffId: createForm.staffId || null,
          customerPhone: createForm.customerPhone.startsWith('+') ? createForm.customerPhone : `+55${createForm.customerPhone}`,
          customerName: createForm.customerName || null,
          scheduledAt: startTime.toISOString(),
          durationMinutes: service?.durationMinutes || 30,
          notes: createForm.notes || undefined,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setIsCreateOpen(false);
        setCreateForm({ customerPhone: '', customerName: '', serviceId: '', staffId: '', date: '', time: '', notes: '' });
        setCreateError(null);
        // Refresh bookings
        const freshData = await fetchBookingsFromApi(selectedBusinessId);
        setBookings(freshData);
      } else {
        const msg = data.error || 'Failed to create booking';
        console.error('Create booking failed:', msg);
        setCreateError(msg);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to create booking';
      console.error('Create booking error:', e);
      setCreateError(msg);
    } finally {
      setCreateLoading(false);
    }
  }, [selectedBusinessId, createForm, availableServices, t]);

  // Reschedule handler
  const handleReschedule = useCallback(async () => {
    setRescheduleError(null);
    if (!rescheduleBookingId || !rescheduleForm.date || !rescheduleForm.time) {
      setRescheduleError(t('bookings.selectNewDateTime', 'Please select a new date and time'));
      return;
    }
    setRescheduleLoading(true);
    try {
      const startTime = new Date(`${rescheduleForm.date}T${rescheduleForm.time}`);
      const response = await apiFetch(`/api/operator/bookings/${rescheduleBookingId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          scheduledAt: startTime.toISOString(),
          staffId: rescheduleForm.staffId || undefined,
          notes: rescheduleForm.notes || undefined,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setIsRescheduleOpen(false);
        setRescheduleBookingId(null);
        setRescheduleError(null);
        const freshData = await fetchBookingsFromApi(selectedBusinessId || undefined);
        setBookings(freshData);
      } else {
        const msg = data.error || 'Failed to reschedule';
        console.error('Reschedule failed:', msg);
        setRescheduleError(msg);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to reschedule';
      console.error('Reschedule error:', e);
      setRescheduleError(msg);
    } finally {
      setRescheduleLoading(false);
    }
  }, [rescheduleBookingId, rescheduleForm, selectedBusinessId, t]);

  const openReschedule = useCallback((booking: BookingListItem) => {
    setRescheduleBookingId(booking.id);
    const d = booking.scheduledAt;
    setRescheduleForm({
      date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
      time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
      staffId: booking.staffId || '',
      notes: booking.notes || '',
    });
    setIsRescheduleOpen(true);
  }, []);

  // Action handlers for booking status updates
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleConfirmBooking = useCallback(async (bookingId: string) => {
    setActionError(null);
    setActionLoading(bookingId);
    const result = await updateBookingStatus(bookingId, 'CONFIRMED');
    setActionLoading(null);

    if (result.success) {
      // Update local state
      setBookings(prev => prev.map(b =>
        b.id === bookingId ? { ...b, status: 'CONFIRMED' as BookingStatus, confirmedAt: new Date() } : b
      ));
      if (selectedBooking?.id === bookingId) {
        setSelectedBooking(prev => prev ? { ...prev, status: 'CONFIRMED' as BookingStatus } : null);
      }
    } else {
      const msg = result.error || 'Failed to confirm booking';
      console.error('Confirm booking failed:', msg);
      setActionError(msg);
    }
  }, [selectedBooking]);

  const handleStartService = useCallback(async (bookingId: string) => {
    setActionError(null);
    setActionLoading(bookingId);
    const result = await updateBookingStatus(bookingId, 'IN_PROGRESS');
    setActionLoading(null);

    if (result.success) {
      setBookings(prev => prev.map(b =>
        b.id === bookingId ? { ...b, status: 'IN_PROGRESS' as BookingStatus } : b
      ));
      if (selectedBooking?.id === bookingId) {
        setSelectedBooking(prev => prev ? { ...prev, status: 'IN_PROGRESS' as BookingStatus } : null);
      }
    } else {
      const msg = result.error || 'Failed to start service';
      console.error('Start service failed:', msg);
      setActionError(msg);
    }
  }, [selectedBooking]);

  const handleCompleteBooking = useCallback(async (bookingId: string) => {
    setActionError(null);
    setActionLoading(bookingId);
    const result = await updateBookingStatus(bookingId, 'COMPLETED');
    setActionLoading(null);

    if (result.success) {
      setBookings(prev => prev.map(b =>
        b.id === bookingId ? { ...b, status: 'COMPLETED' as BookingStatus } : b
      ));
      if (selectedBooking?.id === bookingId) {
        setSelectedBooking(prev => prev ? { ...prev, status: 'COMPLETED' as BookingStatus } : null);
        setIsDetailOpen(false);
      }
    } else {
      const msg = result.error || 'Failed to complete booking';
      console.error('Complete booking failed:', msg);
      setActionError(msg);
    }
  }, [selectedBooking]);

  const handleCancelBooking = useCallback((bookingId: string, reason?: string) => {
    if (reason) {
      // Direct cancel with provided reason (skip dialog)
      executeCancelBooking(bookingId, reason);
    } else {
      // Open cancel dialog to collect reason
      setCancelBookingId(bookingId);
      setCancelReason('');
      setActionError(null);
      setCancelDialogOpen(true);
    }
  }, []);

  const executeCancelBooking = useCallback(async (bookingId: string, cancellationReason: string) => {
    setActionError(null);
    setActionLoading(bookingId);
    const result = await updateBookingStatus(bookingId, 'CANCELLED', cancellationReason);
    setActionLoading(null);

    if (result.success) {
      setCancelDialogOpen(false);
      setCancelBookingId(null);
      setBookings(prev => prev.map(b =>
        b.id === bookingId ? { ...b, status: 'CANCELLED' as BookingStatus, cancellationReason } : b
      ));
      if (selectedBooking?.id === bookingId) {
        setSelectedBooking(prev => prev ? { ...prev, status: 'CANCELLED' as BookingStatus, cancellationReason } : null);
        setIsDetailOpen(false);
      }
    } else {
      const msg = result.error || 'Failed to cancel booking';
      console.error('Cancel booking failed:', msg);
      setActionError(msg);
    }
  }, [selectedBooking]);

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!operator) {
    return null;
  }

  const allStatuses: BookingStatus[] = ['PENDING', 'CONFIRMED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'NO_SHOW'];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('bookings.title')}</h1>
          <p className="text-muted-foreground">
            {selectedBusiness ? selectedBusiness.name : t('common.allBusinesses')} · {t('bookings.bookingCount', { count: filteredBookings.length })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('bookings.newBooking')}
          </Button>
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            {t('common.export')}
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {fetchError && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <p className="text-destructive text-sm">{fetchError}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  setFetchError(null);
                  setLoading(true);
                  try {
                    const data = await fetchBookingsFromApi(selectedBusinessId || undefined);
                    setBookings(data);
                  } catch (e) {
                    setFetchError(e instanceof Error ? e.message : 'Failed to load bookings');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {t('common.retry') || 'Retry'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Error Banner */}
      {actionError && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <p className="text-destructive text-sm">{actionError}</p>
              <Button variant="ghost" size="sm" onClick={() => setActionError(null)}>
                {t('common.close')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('bookings.totalBookings')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('bookings.revenue')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.paidRevenue)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('bookings.completionRate')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats.completionRate >= 0.9 ? 'text-green-600 dark:text-green-500' : ''}`}>
              {(stats.completionRate * 100).toFixed(1)}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('bookings.noShowRate')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats.noShowRate >= 0.1 ? 'text-destructive' : stats.noShowRate <= 0.05 ? 'text-green-600 dark:text-green-500' : 'text-yellow-600 dark:text-yellow-500'}`}>
              {(stats.noShowRate * 100).toFixed(1)}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            {/* Search and Date Range */}
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('bookings.searchPlaceholder')}
                  value={filters.searchQuery}
                  onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={filters.dateRange.preset}
                onValueChange={(value) => handleDateRangeChange(value as DateRangePreset)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('bookings.selectDateRange')} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DATE_RANGE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="ghost" onClick={resetFilters}>
                {t('common.reset')}
              </Button>
            </div>

            {/* Status Filter Chips */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground mr-2">{t('bookings.status')}:</span>
              {allStatuses.map((status) => {
                const statusLabels: Record<BookingStatus, string> = {
                  PENDING: t('bookings.pending'),
                  CONFIRMED: t('bookings.confirmed'),
                  IN_PROGRESS: t('bookings.inProgress'),
                  COMPLETED: t('bookings.completed'),
                  CANCELLED: t('bookings.cancelled'),
                  NO_SHOW: t('bookings.noShow'),
                };
                return (
                  <Button
                    key={status}
                    variant={filters.statuses.includes(status) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleStatus(status)}
                    className="h-7"
                  >
                    {statusLabels[status]}
                  </Button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('customerName')}
                >
                  <div className="flex items-center gap-1">
                    {t('bookings.customer')}
                    {filters.sortBy === 'customerName' && (
                      filters.sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('serviceName')}
                >
                  <div className="flex items-center gap-1">
                    {t('bookings.service')}
                    {filters.sortBy === 'serviceName' && (
                      filters.sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead>{t('bookings.staff')}</TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('scheduledAt')}
                >
                  <div className="flex items-center gap-1">
                    {t('bookings.dateTime')}
                    {filters.sortBy === 'scheduledAt' && (
                      filters.sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('price')}
                >
                  <div className="flex items-center gap-1">
                    {t('bookings.price')}
                    {filters.sortBy === 'price' && (
                      filters.sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead>{t('bookings.status')}</TableHead>
                <TableHead>{t('bookings.payment')}</TableHead>
                <TableHead>{t('bookings.source')}</TableHead>
                <TableHead className="text-right">{t('bookings.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedBookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="h-24 text-center">
                    {t('bookings.noBookingsFound')}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedBookings.map((booking) => {
                  const statusLabels: Record<BookingStatus, string> = {
                    PENDING: t('bookings.pending'),
                    CONFIRMED: t('bookings.confirmed'),
                    IN_PROGRESS: t('bookings.inProgress'),
                    COMPLETED: t('bookings.completed'),
                    CANCELLED: t('bookings.cancelled'),
                    NO_SHOW: t('bookings.noShow'),
                  };
                  return (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <div>
                          <button
                            className="font-medium text-left hover:text-primary hover:underline transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (booking.customerId) {
                                navigate({ to: `/operator/customers/${booking.customerId}` });
                              }
                            }}
                          >
                            {booking.customerName}
                          </button>
                          <div className="text-sm text-muted-foreground">{booking.customerPhone}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div>{booking.serviceName}</div>
                          <div className="text-sm text-muted-foreground">{formatDuration(booking.durationMinutes)}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {booking.staffName || '\u2014'}
                      </TableCell>
                      <TableCell>{formatBookingDate(booking.scheduledAt)}</TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(booking.totalPrice, booking.serviceCurrency)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(booking.status)}>
                          {statusLabels[booking.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant={getPaymentBadgeVariant(booking.paymentStatus)}>
                            {booking.paymentStatus}
                          </Badge>
                          {booking.paymentStatus === 'PAID' && booking.paymentId && (
                            <button
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                              onClick={() => navigate({ to: `/operator/payments/${booking.paymentId}` })}
                              title="View payment details"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span title={BOOKING_SOURCE_LABELS[booking.source]}>
                          {BOOKING_SOURCE_ICONS[booking.source]}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {booking.status === 'PENDING' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400"
                              title={t('bookings.confirmBooking')}
                              onClick={() => handleConfirmBooking(booking.id)}
                              disabled={actionLoading === booking.id}
                            >
                              {actionLoading === booking.id ? (
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          {booking.status === 'CONFIRMED' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-primary hover:text-primary/80"
                              title={t('bookings.startService')}
                              onClick={() => handleStartService(booking.id)}
                              disabled={actionLoading === booking.id}
                            >
                              {actionLoading === booking.id ? (
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          {booking.status === 'IN_PROGRESS' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400"
                              title={t('bookings.complete')}
                              onClick={() => handleCompleteBooking(booking.id)}
                              disabled={actionLoading === booking.id}
                            >
                              {actionLoading === booking.id ? (
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleViewFullDetails(booking.id)}
                            title={t('bookings.viewDetails')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleViewDetails(booking)}
                            title={t('bookings.quickView')}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <div className="text-sm text-muted-foreground">
                {t('common.showingOf', { from: (currentPage - 1) * pageSize + 1, to: Math.min(currentPage * pageSize, filteredBookings.length), total: filteredBookings.length })}
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {t('common.rows', { count: size })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  {t('common.previous')}
                </Button>
                <span className="text-sm text-muted-foreground">
                  {t('common.pageOf', { current: currentPage, total: totalPages })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking Detail Sheet */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          {selectedBooking && (() => {
            const statusLabels: Record<BookingStatus, string> = {
              PENDING: t('bookings.pending'),
              CONFIRMED: t('bookings.confirmed'),
              IN_PROGRESS: t('bookings.inProgress'),
              COMPLETED: t('bookings.completed'),
              CANCELLED: t('bookings.cancelled'),
              NO_SHOW: t('bookings.noShow'),
            };
            return (
              <>
                <SheetHeader>
                  <SheetTitle>{t('bookings.bookingDetails')}</SheetTitle>
                  <SheetDescription>
                    {t('bookings.bookingNumber', { id: selectedBooking.id.slice(-8) })}
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-6">
                  {/* Status Badges */}
                  <div className="flex gap-2">
                    <Badge variant={getStatusBadgeVariant(selectedBooking.status)}>
                      {statusLabels[selectedBooking.status]}
                    </Badge>
                    <Badge variant={getPaymentBadgeVariant(selectedBooking.paymentStatus)}>
                      {selectedBooking.paymentStatus}
                    </Badge>
                  </div>

                  <Separator />

                  {/* Customer */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-muted-foreground">{t('bookings.customer')}</h3>
                    <Card>
                      <CardContent className="pt-4">
                        <button
                          className="font-medium text-left hover:text-primary hover:underline transition-colors"
                          onClick={() => {
                            if (selectedBooking.customerId) {
                              navigate({ to: `/operator/customers/${selectedBooking.customerId}` });
                              setIsDetailOpen(false);
                            }
                          }}
                        >
                          {selectedBooking.customerName}
                        </button>
                        <div className="text-sm text-muted-foreground">{selectedBooking.customerPhone}</div>
                        {selectedBooking.customerEmail && (
                          <div className="text-sm text-muted-foreground">{selectedBooking.customerEmail}</div>
                        )}
                        <div className="mt-3 flex gap-2">
                          <Button variant="outline" size="sm">
                            <MessageSquare className="mr-2 h-4 w-4" />
                            {t('bookings.message')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (selectedBooking.customerId) {
                                navigate({ to: `/operator/customers/${selectedBooking.customerId}` });
                                setIsDetailOpen(false);
                              }
                            }}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            {t('bookings.viewProfile')}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Service */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-muted-foreground">{t('bookings.service')}</h3>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="font-medium">{selectedBooking.serviceName}</div>
                        <div className="text-sm text-muted-foreground">
                          {formatDuration(selectedBooking.durationMinutes)} · {selectedBooking.staffName || t('bookings.noStaffAssigned')}
                        </div>
                        <div className="mt-2 text-lg font-semibold">
                          {formatCurrency(selectedBooking.totalPrice, selectedBooking.serviceCurrency)}
                        </div>
                        {selectedBooking.depositAmount && (
                          <div className="text-sm text-muted-foreground">
                            {t('bookings.deposit')}: {formatCurrency(selectedBooking.depositAmount, selectedBooking.serviceCurrency)}
                          </div>
                        )}
                        {selectedBooking.tipAmount && (
                          <div className="text-sm text-green-600 dark:text-green-500">
                            {t('bookings.tip')}: {formatCurrency(selectedBooking.tipAmount, selectedBooking.serviceCurrency)}
                          </div>
                        )}
                        {selectedBooking.paymentStatus === 'PAID' && selectedBooking.paymentId && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-3"
                            onClick={() => {
                              navigate({ to: `/operator/payments/${selectedBooking.paymentId}` });
                              setIsDetailOpen(false);
                            }}
                          >
                            <CreditCard className="mr-2 h-4 w-4" />
                            View Payment
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Schedule */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-muted-foreground">{t('bookings.schedule')}</h3>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="font-medium">
                          {selectedBooking.scheduledAt.toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </div>
                        <div className="text-muted-foreground">
                          {selectedBooking.scheduledAt.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                          })} – {selectedBooking.endsAt.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </div>
                        <div className="mt-2 text-sm text-muted-foreground">
                          {t('bookings.source')}: {BOOKING_SOURCE_ICONS[selectedBooking.source]} {BOOKING_SOURCE_LABELS[selectedBooking.source]}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Notes */}
                  {(selectedBooking.notes || selectedBooking.cancellationReason) && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium text-muted-foreground">{t('bookings.notes')}</h3>
                      <Card>
                        <CardContent className="pt-4">
                          {selectedBooking.notes && (
                            <p className="text-sm">{selectedBooking.notes}</p>
                          )}
                          {selectedBooking.cancellationReason && (
                            <p className="text-sm text-destructive">
                              {t('bookings.cancellation')}: {selectedBooking.cancellationReason}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <div>{t('bookings.business')}: {selectedBooking.businessName}</div>
                    <div>{t('bookings.created')}: {selectedBooking.createdAt.toLocaleString()}</div>
                  </div>

                  <Separator />

                  {/* Actions */}
                  <div className="flex gap-2">
                    {selectedBooking.status === 'PENDING' && (
                      <>
                        <Button
                          className="flex-1"
                          onClick={() => handleConfirmBooking(selectedBooking.id)}
                          disabled={actionLoading === selectedBooking.id}
                        >
                          {actionLoading === selectedBooking.id ? (
                            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          ) : (
                            <Check className="mr-2 h-4 w-4" />
                          )}
                          {t('bookings.confirmBooking')}
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => handleCancelBooking(selectedBooking.id)}
                          disabled={actionLoading === selectedBooking.id}
                        >
                          <X className="mr-2 h-4 w-4" />
                          {t('bookings.cancelBooking')}
                        </Button>
                      </>
                    )}
                    {selectedBooking.status === 'CONFIRMED' && (
                      <>
                        <Button
                          className="flex-1"
                          onClick={() => handleStartService(selectedBooking.id)}
                          disabled={actionLoading === selectedBooking.id}
                        >
                          {actionLoading === selectedBooking.id ? (
                            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          ) : (
                            <Play className="mr-2 h-4 w-4" />
                          )}
                          {t('bookings.startService')}
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => handleCancelBooking(selectedBooking.id)}
                          disabled={actionLoading === selectedBooking.id}
                        >
                          <X className="mr-2 h-4 w-4" />
                          {t('bookings.cancelBooking')}
                        </Button>
                      </>
                    )}
                    {selectedBooking.status === 'IN_PROGRESS' && (
                      <Button
                        className="flex-1"
                        onClick={() => handleCompleteBooking(selectedBooking.id)}
                        disabled={actionLoading === selectedBooking.id}
                      >
                        {actionLoading === selectedBooking.id ? (
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        ) : (
                          <Check className="mr-2 h-4 w-4" />
                        )}
                        {t('bookings.complete')}
                      </Button>
                    )}
                    {['PENDING', 'CONFIRMED'].includes(selectedBooking.status) && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsDetailOpen(false);
                          openReschedule(selectedBooking);
                        }}
                      >
                        <CalendarClock className="mr-2 h-4 w-4" />
                        {t('bookings.reschedule')}
                      </Button>
                    )}
                    {['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(selectedBooking.status) && (
                      <Button variant="outline" className="flex-1">
                        {t('bookings.bookAgain')}
                      </Button>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      {/* Create Booking Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('bookings.newBooking')}</DialogTitle>
            <DialogDescription>{t('bookings.createDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('bookings.customerPhone')} *</Label>
                <Input
                  placeholder="+5511999999999"
                  value={createForm.customerPhone}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, customerPhone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('bookings.customerName')}</Label>
                <Input
                  placeholder={t('bookings.namePlaceholder')}
                  value={createForm.customerName}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, customerName: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('bookings.service')} *</Label>
              <Select value={createForm.serviceId} onValueChange={(v) => setCreateForm(prev => ({ ...prev, serviceId: v }))}>
                <SelectTrigger><SelectValue placeholder={t('bookings.selectService')} /></SelectTrigger>
                <SelectContent>
                  {availableServices.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} ({s.durationMinutes}min · R${(s.priceCents / 100).toFixed(2)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('bookings.staff')}</Label>
              <Select value={createForm.staffId} onValueChange={(v) => setCreateForm(prev => ({ ...prev, staffId: v }))}>
                <SelectTrigger><SelectValue placeholder={t('bookings.selectStaff')} /></SelectTrigger>
                <SelectContent>
                  {availableStaff.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('bookings.date')} *</Label>
                <Input
                  type="date"
                  value={createForm.date}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('bookings.time')} *</Label>
                <Input
                  type="time"
                  value={createForm.time}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, time: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('bookings.notes')}</Label>
              <Textarea
                placeholder={t('bookings.notesPlaceholder')}
                value={createForm.notes}
                onChange={(e) => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          {createError && (
            <div className="bg-destructive/15 text-destructive px-3 py-2 rounded-md text-sm">
              {createError}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsCreateOpen(false); setCreateError(null); }}>{t('common.cancel')}</Button>
            <Button onClick={handleCreateBooking} disabled={createLoading}>
              {createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('bookings.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Booking Dialog */}
      <Dialog open={isRescheduleOpen} onOpenChange={setIsRescheduleOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('bookings.reschedule')}</DialogTitle>
            <DialogDescription>{t('bookings.rescheduleDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('bookings.newDate')} *</Label>
                <Input
                  type="date"
                  value={rescheduleForm.date}
                  onChange={(e) => setRescheduleForm(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('bookings.newTime')} *</Label>
                <Input
                  type="time"
                  value={rescheduleForm.time}
                  onChange={(e) => setRescheduleForm(prev => ({ ...prev, time: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('bookings.staff')}</Label>
              <Select value={rescheduleForm.staffId} onValueChange={(v) => setRescheduleForm(prev => ({ ...prev, staffId: v }))}>
                <SelectTrigger><SelectValue placeholder={t('bookings.selectStaff')} /></SelectTrigger>
                <SelectContent>
                  {availableStaff.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('bookings.notes')}</Label>
              <Textarea
                value={rescheduleForm.notes}
                onChange={(e) => setRescheduleForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          {rescheduleError && (
            <div className="bg-destructive/15 text-destructive px-3 py-2 rounded-md text-sm">
              {rescheduleError}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsRescheduleOpen(false); setRescheduleError(null); }}>{t('common.cancel')}</Button>
            <Button onClick={handleReschedule} disabled={rescheduleLoading}>
              {rescheduleLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('bookings.confirmReschedule')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Reason Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={(open) => { if (!open) { setCancelDialogOpen(false); setCancelBookingId(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('bookings.cancelBooking')}</DialogTitle>
            <DialogDescription>{t('bookings.cancelReasonPrompt', 'Please provide a reason for cancellation.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('bookings.cancellationReason', 'Reason')}</Label>
              <Textarea
                placeholder={t('bookings.cancelReasonPlaceholder', 'Enter cancellation reason...')}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
              />
            </div>
            {actionError && (
              <div className="bg-destructive/15 text-destructive px-3 py-2 rounded-md text-sm">
                {actionError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelDialogOpen(false); setCancelBookingId(null); }}>{t('common.cancel')}</Button>
            <Button
              variant="destructive"
              onClick={() => { if (cancelBookingId && cancelReason.trim()) executeCancelBooking(cancelBookingId, cancelReason.trim()); }}
              disabled={!cancelReason.trim() || (actionLoading === cancelBookingId)}
            >
              {actionLoading === cancelBookingId && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('bookings.cancelBooking')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
